// Cell formatting for the dashboard widget renderer (arc 101 slice 09, task #81).
// A widget's `columns[].format` / `stat.format` names one of the grammar's
// `ColumnFormat`s; this maps a raw result cell (opaque JSON) to a display string.
// Pure + defensive: a value that doesn't fit its declared format falls back to
// its string form rather than throwing — a drifted resolver never breaks a render.

import type { ColumnFormat } from '@developerz/domain';
import { t } from '../i18n';
import { UNSET } from './unset';

const NUMBER = new Intl.NumberFormat('en-US');
const CURRENCY = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
const PERCENT = new Intl.NumberFormat('en-US', { style: 'percent', maximumFractionDigits: 1 });
// `date` is a CALENDAR date — format in UTC so `2026-07-10` (parsed as UTC
// midnight) never rolls back a day in a negative-offset zone. `datetime` stays
// localized: an instant is meaningfully shown in the viewer's own time zone.
const DATE = new Intl.DateTimeFormat('en-US', {
  timeZone: 'UTC',
  year: 'numeric',
  month: 'numeric',
  day: 'numeric',
});

function asNumber(value: unknown): number | null {
  const n = typeof value === 'number' ? value : typeof value === 'string' ? Number(value) : NaN;
  return Number.isFinite(n) ? n : null;
}

function asDate(value: unknown): Date | null {
  if (typeof value !== 'string' && typeof value !== 'number') return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Format one result-set cell for display. `text` (the default) stringifies. */
export function formatCell(value: unknown, format: ColumnFormat = 'text'): string {
  if (value === null || value === undefined || value === '') return UNSET;
  switch (format) {
    case 'number': {
      const n = asNumber(value);
      return n === null ? String(value) : NUMBER.format(n);
    }
    case 'currency': {
      const n = asNumber(value);
      return n === null ? String(value) : CURRENCY.format(n);
    }
    case 'percent': {
      const n = asNumber(value);
      return n === null ? String(value) : PERCENT.format(n);
    }
    case 'boolean':
      // Through the catalog, never a literal. `i18n.coverage.test.ts` can only
      // see static `t()` calls, so an English string returned from a formatter
      // was exactly the kind of copy that gate cannot find — it rendered "Yes"
      // beside `t()`-translated column headers on the Spanish console.
      return value === true || value === 'true' || value === 1 ? t('widgets.yes') : t('widgets.no');
    case 'date': {
      const d = asDate(value);
      return d === null ? String(value) : DATE.format(d);
    }
    case 'datetime': {
      const d = asDate(value);
      return d === null ? String(value) : d.toLocaleString('en-US');
    }
    default:
      return String(value);
  }
}
